/*
 * Wizard Wonders "Observatory" behaviours.
 * Custom elements, no libraries:
 *   <ww-starfield>       hero night sky: twinkling stars, constellation lines near the pointer,
 *                        shooting stars and a gentle parallax on the hero art
 *   <ww-moon-phase>      fills any [data-ww-moon] text and draws [data-ww-moon-canvas] moons
 *   <ww-cosmic-zoom>     "How big is everything?" slider
 *   <ww-calendar-months> twelve painted month thumbnails
 *   <ww-product-card>    keeps the price and button in step with the chosen option
 *
 * Every animated canvas pauses when it is off screen or the tab is hidden, the device pixel
 * ratio is capped at 2, and nothing animates when the visitor prefers reduced motion or the
 * "Animate the night sky" theme setting is off.
 */
(() => {
  'use strict';
  if (window.WWObservatory) return;

  const TAU = Math.PI * 2;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOff = () => reduceQuery.matches || document.documentElement.dataset.wwMotion === 'off';
  const dpr = () => Math.min(window.devicePixelRatio || 1, 2);

  function rng(seed) {
    let s = seed >>> 0 || 1;
    return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
  }

  function fit(cv, ratio) {
    const r = cv.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width * ratio));
    const h = Math.max(1, Math.round(r.height * ratio));
    if (cv.width !== w || cv.height !== h) {
      cv.width = w;
      cv.height = h;
    }
    return [w, h];
  }

  function glowDot(ctx, x, y, r, col) {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, col);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }

  /* ------------------------------------------------------------------
   * Base class: runs frame() on requestAnimationFrame only while the
   * element is on screen, the tab is visible and motion is allowed.
   * ------------------------------------------------------------------ */
  class LiveCanvas extends HTMLElement {
    constructor() {
      super();
      this.raf = 0;
      this.inView = false;
      this.tick = this.tick.bind(this);
      this.sync = this.sync.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
    }

    connectedCallback() {
      this.ratio = dpr();
      this.setup();
      this.io = new IntersectionObserver(
        (entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          this.sync();
        },
        { rootMargin: '80px 0px' }
      );
      this.io.observe(this);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.handleResize);
        this.ro.observe(this);
      } else {
        window.addEventListener('resize', this.handleResize);
      }
      document.addEventListener('visibilitychange', this.sync);
      if (reduceQuery.addEventListener) reduceQuery.addEventListener('change', this.handleMotionChange);
      this.drawStill();
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.sync);
      if (reduceQuery.removeEventListener) reduceQuery.removeEventListener('change', this.handleMotionChange);
      this.teardown();
    }

    get running() {
      return this.inView && !document.hidden && !motionOff();
    }

    sync() {
      if (this.running) {
        if (!this.raf) this.raf = requestAnimationFrame(this.tick);
      } else {
        this.stop();
      }
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    tick(t) {
      this.raf = requestAnimationFrame(this.tick);
      this.frame(t);
    }

    handleResize() {
      this.ratio = dpr();
      this.resized();
      if (!this.raf) this.drawStill();
    }

    handleMotionChange() {
      this.sync();
      if (!this.raf) this.drawStill();
    }

    /* hooks */
    setup() {}
    teardown() {}
    resized() {}
    frame() {}
    drawStill() {
      this.frame(0);
    }
  }

  /* ------------------------------------------------------------------
   * Hero sky
   * ------------------------------------------------------------------ */
  class WWStarfield extends LiveCanvas {
    setup() {
      this.canvas = this.querySelector('canvas[data-ww-sky]');
      this.art = this.querySelector('[data-ww-parallax]');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this.density = Number(this.dataset.density) || 9000;
      this.mouse = { x: -1e4, y: -1e4 };
      this.shoot = null;
      this.seed();
      this.onMove = (e) => {
        const r = this.canvas.getBoundingClientRect();
        this.mouse.x = (e.clientX - r.left) * this.ratio;
        this.mouse.y = (e.clientY - r.top) * this.ratio;
        if (!motionOff() && this.art) {
          const dx = e.clientX / window.innerWidth - 0.5;
          const dy = e.clientY / window.innerHeight - 0.5;
          this.art.style.transform = `translate3d(${-dx * 14}px, ${-dy * 10}px, 0) scale(1.02)`;
        }
        if (!this.raf) this.drawStill();
      };
      this.onLeave = () => {
        this.mouse.x = this.mouse.y = -1e4;
        if (!this.raf) this.drawStill();
      };
      this.addEventListener('pointermove', this.onMove);
      this.addEventListener('pointerleave', this.onLeave);
    }

    teardown() {
      this.removeEventListener('pointermove', this.onMove);
      this.removeEventListener('pointerleave', this.onLeave);
    }

    seed() {
      if (!this.canvas) return;
      const [w, h] = fit(this.canvas, this.ratio);
      const r = rng(7);
      const n = Math.min(900, Math.round((w * h) / (this.density * this.ratio)));
      this.stars = Array.from({ length: n }, () => ({
        x: r() * w,
        y: r() * h,
        z: 0.3 + r() * 0.7,
        p: r() * TAU,
        s: 0.4 + r() * 1.4,
      }));
    }

    resized() {
      this.seed();
    }

    frame(t) {
      if (!this.ctx) return;
      const still = motionOff() || !this.raf;
      const ctx = this.ctx;
      const R = this.ratio;
      const w = this.canvas.width;
      const h = this.canvas.height;
      ctx.clearRect(0, 0, w, h);
      for (const s of this.stars) {
        const tw = still ? 0.8 : 0.55 + 0.45 * Math.sin(t * 0.0015 * s.s + s.p);
        ctx.globalAlpha = tw * s.z;
        ctx.fillStyle = s.z > 0.85 ? '#F7D98A' : '#F5EEDC';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.z * 1.3 * R, 0, TAU);
        ctx.fill();
      }
      // constellation lines around the pointer
      const m = this.mouse;
      const near = this.stars.filter((s) => Math.hypot(s.x - m.x, s.y - m.y) < 170 * R).slice(0, 14);
      ctx.strokeStyle = '#F2C14E';
      ctx.lineWidth = 0.8 * R;
      for (let i = 0; i < near.length; i++) {
        for (let j = i + 1; j < near.length; j++) {
          const d = Math.hypot(near[i].x - near[j].x, near[i].y - near[j].y);
          if (d < 90 * R) {
            ctx.globalAlpha = 0.55 * (1 - d / (90 * R));
            ctx.beginPath();
            ctx.moveTo(near[i].x, near[i].y);
            ctx.lineTo(near[j].x, near[j].y);
            ctx.stroke();
          }
        }
      }
      // shooting star
      if (!still) {
        if (!this.shoot && Math.random() < 0.004) {
          this.shoot = {
            x: w * (0.35 + Math.random() * 0.6),
            y: h * Math.random() * 0.4,
            vx: -(7 + Math.random() * 5) * R,
            vy: (3 + Math.random() * 2) * R,
            life: 1,
          };
        }
        const s = this.shoot;
        if (s) {
          const g = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * 14, s.y - s.vy * 14);
          g.addColorStop(0, 'rgba(255,248,220,.95)');
          g.addColorStop(1, 'rgba(255,248,220,0)');
          ctx.globalAlpha = s.life;
          ctx.strokeStyle = g;
          ctx.lineWidth = 1.6 * R;
          ctx.beginPath();
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(s.x - s.vx * 14, s.y - s.vy * 14);
          ctx.stroke();
          s.x += s.vx;
          s.y += s.vy;
          s.life -= 0.012;
          if (s.life <= 0) this.shoot = null;
        }
      }
      ctx.globalAlpha = 1;
    }
  }

  /* ------------------------------------------------------------------
   * Moon phase: mean synodic month counted from the new Moon of
   * 6 Jan 2000, 18:14 UTC. Good to about a day.
   * ------------------------------------------------------------------ */
  const SYN = 29.530588853;
  const NEW0 = Date.UTC(2000, 0, 6, 18, 14) / 864e5;

  function moonNow(now = new Date()) {
    const day = now.getTime() / 864e5;
    const age = (((day - NEW0) % SYN) + SYN) % SYN;
    const lit = (1 - Math.cos((TAU * age) / SYN)) / 2;
    const names = [
      [1.84, 'a new Moon'],
      [5.53, 'a waxing crescent'],
      [9.23, 'at first quarter'],
      [12.92, 'a waxing gibbous'],
      [16.61, 'full'],
      [20.3, 'a waning gibbous'],
      [24.0, 'at last quarter'],
      [27.69, 'a waning crescent'],
      [SYN, 'a new Moon'],
    ];
    const phase = names.find((n) => age < n[0])[1];
    const short = phase.replace(/^(a |at )/, '').replace(/^./, (c) => c.toUpperCase());
    let fullIn = SYN / 2 - age;
    if (fullIn < 0) fullIn += SYN;
    const nextFull = new Date(now.getTime() + fullIn * 864e5);
    const lang = (document.documentElement.lang || 'en').toLowerCase();
    const locale = lang === 'en' || lang.startsWith('en-') ? 'en-GB' : lang;
    let fullText;
    try {
      fullText = nextFull.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    } catch (e) {
      fullText = nextFull.toDateString().slice(4, 10);
    }
    const line =
      lit > 0.9
        ? 'Bright enough to cast shadows. Look for the dark "seas" of old lava on its face.'
        : lit < 0.08
        ? 'A dark night: the best time to find the Milky Way away from city lights.'
        : age < SYN / 2
        ? 'Look west after sunset. The line between light and dark is where craters stand out best through binoculars.'
        : 'Rising later each night. Early risers will find it high in the morning sky.';
    return { age, lit, phase, short, fullText, line, pct: Math.round(lit * 100) + '%' };
  }

  function drawMoon(cv, age, ratio) {
    const [w, h] = fit(cv, ratio);
    const ctx = cv.getContext('2d');
    const R = Math.min(w, h) * 0.42;
    const cx = w / 2;
    const cy = h / 2;
    ctx.clearRect(0, 0, w, h);
    const glow = ctx.createRadialGradient(cx, cy, R * 0.9, cx, cy, R * 1.25);
    glow.addColorStop(0, 'rgba(245,238,220,.18)');
    glow.addColorStop(1, 'rgba(245,238,220,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.25, 0, TAU);
    ctx.fill();
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, TAU);
    ctx.clip();
    const base = ctx.createRadialGradient(cx - R * 0.25, cy - R * 0.3, R * 0.1, cx, cy, R);
    base.addColorStop(0, '#F3EBD8');
    base.addColorStop(1, '#B9AE98');
    ctx.fillStyle = base;
    ctx.fillRect(cx - R, cy - R, 2 * R, 2 * R);
    const r = rng(42);
    ctx.fillStyle = 'rgba(110,102,92,.35)';
    [
      [-0.25, -0.25, 0.32],
      [0.15, -0.35, 0.22],
      [0.3, 0.05, 0.26],
      [-0.05, 0.15, 0.2],
      [-0.35, 0.25, 0.16],
      [0.12, 0.42, 0.12],
    ].forEach(([x, y, s]) => {
      ctx.beginPath();
      ctx.ellipse(cx + x * R, cy + y * R, s * R, s * R * 0.8, r() * 3, 0, TAU);
      ctx.fill();
    });
    for (let i = 0; i < 70; i++) {
      const x = cx + (r() * 2 - 1) * R;
      const y = cy + (r() * 2 - 1) * R;
      const s = (0.01 + r() * 0.04) * R;
      ctx.fillStyle = 'rgba(90,82,74,.28)';
      ctx.beginPath();
      ctx.arc(x, y, s, 0, TAU);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,250,235,.25)';
      ctx.beginPath();
      ctx.arc(x - s * 0.3, y - s * 0.3, s * 0.6, 0, TAU);
      ctx.fill();
    }
    // Shadow: terminator drawn as an ellipse. Northern Hemisphere view (waxing = lit on the right).
    const k = Math.cos((TAU * age) / SYN);
    const waxing = age < SYN / 2;
    ctx.fillStyle = 'rgba(8,10,26,.94)';
    ctx.beginPath();
    ctx.arc(cx, cy, R + 1, waxing ? Math.PI / 2 : -Math.PI / 2, waxing ? -Math.PI / 2 : Math.PI / 2, false);
    ctx.ellipse(cx, cy, Math.abs(k) * R + 0.5, R + 1, 0, waxing ? -Math.PI / 2 : Math.PI / 2, waxing ? Math.PI / 2 : -Math.PI / 2, k < 0);
    ctx.fill();
    ctx.restore();
  }

  class WWMoonPhase extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      this.render();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.drawCanvases());
        this.ro.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
    }

    render() {
      this.m = moonNow();
      const m = this.m;
      const map = {
        phase: m.phase,
        short: m.short,
        lit: m.pct,
        'lit-label': m.pct + ' lit',
        age: m.age.toFixed(1),
        full: m.fullText,
        line: m.line,
      };
      this.querySelectorAll('[data-ww-moon]').forEach((el) => {
        const key = el.getAttribute('data-ww-moon');
        if (key in map) el.textContent = map[key];
      });
      this.querySelectorAll('[data-ww-moon-label]').forEach((el) => {
        el.setAttribute('aria-label', `${el.getAttribute('data-ww-moon-label')}: ${m.short}, ${m.pct} lit`);
      });
      this.drawCanvases();
    }

    drawCanvases() {
      if (!this.m) return;
      this.querySelectorAll('canvas[data-ww-moon-canvas]').forEach((cv) => drawMoon(cv, this.m.age, dpr()));
    }
  }

  /* ------------------------------------------------------------------
   * Cosmic zoom
   * ------------------------------------------------------------------ */
  const STOPS = [
    { name: 'Earth', size: '12,742 km', sizeL: 'across', light: '0.04 seconds', view: 'View ≈ 40,000 km wide',
      say: 'Every person who has ever lived did so on this small, bright ball. Hold that thought. We are going outward.' },
    { name: 'The Earth and the Moon', size: '384,400 km', sizeL: 'average distance to the Moon', light: '1.3 seconds', view: 'View ≈ 1 million km wide',
      say: 'All of the planets in our Solar System would fit, side by side, in the gap between us and the Moon.' },
    { name: 'Earth’s orbit', size: '150 million km', sizeL: 'from the Sun (1 AU)', light: '8 min 20 s', view: 'View ≈ 400 million km wide',
      say: 'The sunlight warming your face left the Sun about eight minutes ago. You are always seeing the past.' },
    { name: 'The planets', size: '4.5 billion km', sizeL: 'from the Sun to Neptune', light: 'about 4 hours', view: 'View ≈ 10 billion km wide',
      say: 'Orbits drawn here are true to their ratios. Most of the Solar System is empty, quiet space.' },
    { name: 'Our nearest star', size: '4.24 light-years', sizeL: 'to Proxima Centauri', light: '4.24 years', view: 'View ≈ 12 light-years wide',
      say: 'Our fastest spacecraft would need many thousands of years to get there. And this is next door.' },
    { name: 'The Milky Way', size: '100,000 light-years', sizeL: 'across, roughly', light: '100,000 years', view: 'View ≈ 150,000 light-years wide',
      say: 'Our Sun is one of a few hundred billion stars, about 26,000 light-years out from the centre.' },
    { name: 'Andromeda', size: '2.5 million light-years', sizeL: 'away', light: '2.5 million years', view: 'View ≈ 4 million light-years wide',
      say: 'The light you see from Andromeda tonight set off before the first humans walked the Earth.' },
    { name: 'The observable universe', size: '93 billion light-years', sizeL: 'across', light: 'more than its age', view: 'View ≈ everything we can see',
      say: 'Galaxies gather along threads around vast empty voids. And beyond the edge of what we can see, there is surely more.' },
  ];

  const bgStars = (() => {
    const r = rng(11);
    return Array.from({ length: 420 }, () => [r(), r(), r()]);
  })();
  const galaxy = (() => {
    const r = rng(5);
    const pts = [];
    for (let i = 0; i < 2600; i++) {
      const arm = i % 2;
      const d = Math.pow(r(), 0.6);
      const a = d * 5.2 + arm * Math.PI + (r() - 0.5) * 0.55;
      pts.push([Math.cos(a) * d, Math.sin(a) * d, r()]);
    }
    for (let i = 0; i < 500; i++) {
      const d = Math.pow(r(), 2) * 0.25;
      const a = r() * TAU;
      pts.push([Math.cos(a) * d, Math.sin(a) * d, r()]);
    }
    return pts;
  })();
  const web = (() => {
    const r = rng(9);
    const pts = Array.from({ length: 240 }, () => [r() * 2 - 1, r() * 2 - 1]);
    const lines = [];
    pts.forEach((p, i) => {
      pts
        .map((q, j) => [j, Math.hypot(p[0] - q[0], p[1] - q[1])])
        .filter((x) => x[0] !== i)
        .sort((a, b) => a[1] - b[1])
        .slice(0, 2)
        .forEach(([j, d]) => {
          if (d < 0.22) lines.push([p, pts[j]]);
        });
    });
    return { pts, lines };
  })();

  function zoomBg(ctx, w, h, dens, R) {
    for (const [x, y, b] of bgStars) {
      if (b > dens) continue;
      ctx.globalAlpha = 0.25 + b * 0.6;
      ctx.fillStyle = '#F5EEDC';
      const s = (b > 0.9 ? 2 : 1) * R;
      ctx.fillRect(x * w, y * h, s, s);
    }
    ctx.globalAlpha = 1;
  }

  function earth(ctx, x, y, R, t) {
    glowDot(ctx, x, y, R * 1.25, 'rgba(120,180,255,.35)');
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, R, 0, TAU);
    ctx.clip();
    const g = ctx.createRadialGradient(x - R * 0.35, y - R * 0.35, R * 0.1, x, y, R);
    g.addColorStop(0, '#5FA8E8');
    g.addColorStop(1, '#163A78');
    ctx.fillStyle = g;
    ctx.fillRect(x - R, y - R, 2 * R, 2 * R);
    const r = rng(3);
    ctx.fillStyle = '#3E7D4E';
    for (let i = 0; i < 7; i++) {
      const ax = x + (r() * 2 - 1) * R * 0.8 + ((t * 0.00002 * R) % R);
      const ay = y + (r() * 2 - 1) * R * 0.7;
      ctx.beginPath();
      ctx.ellipse(ax, ay, R * (0.15 + r() * 0.25), R * (0.08 + r() * 0.18), r() * 3, 0, TAU);
      ctx.fill();
    }
    ctx.strokeStyle = 'rgba(255,255,255,.55)';
    ctx.lineCap = 'round';
    for (let i = 0; i < 9; i++) {
      ctx.lineWidth = R * (0.03 + r() * 0.05);
      ctx.beginPath();
      const yy = y + (r() * 2 - 1) * R * 0.8;
      ctx.arc(x + (r() - 0.5) * R, yy, R * (0.3 + r() * 0.5), r() * 3, r() * 3 + 1.2);
      ctx.stroke();
    }
    const sh = ctx.createLinearGradient(x - R, y, x + R, y);
    sh.addColorStop(0, 'rgba(0,0,0,0)');
    sh.addColorStop(0.62, 'rgba(0,0,0,.05)');
    sh.addColorStop(1, 'rgba(4,6,20,.75)');
    ctx.fillStyle = sh;
    ctx.fillRect(x - R, y - R, 2 * R, 2 * R);
    ctx.restore();
  }

  function label(ctx, txt, x, y, col, R, font) {
    ctx.font = `600 ${12 * R}px ${font}`;
    ctx.fillStyle = col || '#B8B7CF';
    ctx.textAlign = 'center';
    ctx.fillText(txt, x, y);
  }

  function scene(i, ctx, w, h, t, R, font) {
    const cx = w / 2;
    const cy = h / 2;
    const m = Math.min(w, h);
    const L = (txt, x, y, col) => label(ctx, txt, x, y, col, R, font);
    switch (i) {
      case 0:
        zoomBg(ctx, w, h, 0.6, R);
        earth(ctx, cx, cy, m * 0.32, t);
        break;
      case 1: {
        zoomBg(ctx, w, h, 0.7, R);
        const ex = cx - m * 0.42;
        const mx = cx + m * 0.42;
        ctx.setLineDash([4 * R, 6 * R]);
        ctx.strokeStyle = 'rgba(245,238,220,.3)';
        ctx.lineWidth = R;
        ctx.beginPath();
        ctx.arc(ex, cy, mx - ex, -0.5, 0.5);
        ctx.stroke();
        ctx.setLineDash([]);
        earth(ctx, ex, cy, m * 0.034, t);
        ctx.fillStyle = '#CFC6B4';
        ctx.beginPath();
        ctx.arc(mx, cy, m * 0.0093, 0, TAU);
        ctx.fill();
        L('Earth', ex, cy + m * 0.08);
        L('Moon', mx, cy + m * 0.08);
        L('384,400 km', cx, cy - m * 0.05, '#F2C14E');
        break;
      }
      case 2: {
        zoomBg(ctx, w, h, 0.8, R);
        glowDot(ctx, cx, cy, m * 0.12, 'rgba(255,200,90,.9)');
        glowDot(ctx, cx, cy, m * 0.03, '#FFF3C8');
        [
          [0.39, '#C9B9A6', 'Mercury'],
          [0.72, '#E8C98A', 'Venus'],
          [1, '#5FA8E8', 'Earth'],
        ].forEach(([a, c], k) => {
          const rad = a * m * 0.4;
          ctx.strokeStyle = 'rgba(245,238,220,.22)';
          ctx.lineWidth = R;
          ctx.beginPath();
          ctx.arc(cx, cy, rad, 0, TAU);
          ctx.stroke();
          const ang = t * 0.0002 * (1 / Math.pow(a, 1.5)) + k * 2;
          const px = cx + Math.cos(ang) * rad;
          const py = cy + Math.sin(ang) * rad;
          ctx.fillStyle = c;
          ctx.beginPath();
          ctx.arc(px, py, (k === 2 ? 4 : 3) * R, 0, TAU);
          ctx.fill();
          if (k === 2) L('Earth', px, py - 10 * R, '#F5EEDC');
        });
        L('Sun', cx, cy + m * 0.07, '#F2C14E');
        break;
      }
      case 3: {
        zoomBg(ctx, w, h, 0.85, R);
        glowDot(ctx, cx, cy, m * 0.03, '#FFE9A8');
        [
          [0.39, '#C9B9A6'],
          [0.72, '#E8C98A'],
          [1, '#5FA8E8'],
          [1.52, '#E8743B'],
          [5.2, '#D9B48A'],
          [9.58, '#E8D29A'],
          [19.2, '#9FD8E0'],
          [30.1, '#5E7FD6'],
        ].forEach(([a, c], k) => {
          const rad = (a / 30.1) * m * 0.46;
          ctx.strokeStyle = 'rgba(245,238,220,.18)';
          ctx.lineWidth = R;
          ctx.beginPath();
          ctx.arc(cx, cy, rad, 0, TAU);
          ctx.stroke();
          const ang = (t * 0.00025) / Math.pow(a, 1.5) + k * 1.7;
          ctx.fillStyle = c;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(ang) * rad, cy + Math.sin(ang) * rad, (k > 3 ? 3.2 : 1.6) * R, 0, TAU);
          ctx.fill();
        });
        L('Neptune’s orbit', cx, cy - m * 0.48, '#9FB6F0');
        break;
      }
      case 4: {
        zoomBg(ctx, w, h, 1, R);
        const sx = cx - m * 0.3;
        const px = cx + m * 0.3;
        glowDot(ctx, sx, cy, m * 0.05, 'rgba(255,220,140,.9)');
        glowDot(ctx, px, cy, m * 0.04, 'rgba(255,140,110,.85)');
        ctx.strokeStyle = 'rgba(242,193,78,.6)';
        ctx.setLineDash([5 * R, 6 * R]);
        ctx.beginPath();
        ctx.moveTo(sx + m * 0.05, cy);
        ctx.lineTo(px - m * 0.05, cy);
        ctx.stroke();
        ctx.setLineDash([]);
        L('Sun', sx, cy + m * 0.09, '#F5EEDC');
        L('Proxima Centauri', px, cy + m * 0.09, '#F5EEDC');
        L('4.24 light-years', cx, cy - m * 0.04, '#F2C14E');
        break;
      }
      case 5: {
        zoomBg(ctx, w, h, 1, R);
        const rot = t * 0.00003;
        const rad = m * 0.44;
        for (const [x, y, b] of galaxy) {
          const a = Math.atan2(y, x) + rot;
          const d = Math.hypot(x, y);
          const X = cx + Math.cos(a) * d * rad;
          const Y = cy + Math.sin(a) * d * rad * 0.62;
          ctx.globalAlpha = 0.25 + b * 0.6;
          ctx.fillStyle = d < 0.25 ? '#F7D98A' : b > 0.7 ? '#C9B6FF' : '#9FB6F0';
          ctx.fillRect(X, Y, 1.3 * R, 1.3 * R);
        }
        ctx.globalAlpha = 1;
        glowDot(ctx, cx, cy, m * 0.08, 'rgba(255,224,160,.55)');
        const ya = 0.52 * Math.PI + rot;
        const yx = cx + Math.cos(ya) * 0.52 * rad;
        const yy = cy + Math.sin(ya) * 0.52 * rad * 0.62;
        ctx.strokeStyle = '#F2C14E';
        ctx.lineWidth = 1.5 * R;
        ctx.beginPath();
        ctx.arc(yx, yy, 6 * R, 0, TAU);
        ctx.stroke();
        L('You are here', yx, yy + 20 * R, '#F2C14E');
        break;
      }
      case 6: {
        zoomBg(ctx, w, h, 1, R);
        const g1 = [cx - m * 0.34, cy + m * 0.05];
        const g2 = [cx + m * 0.26, cy - m * 0.06];
        [
          [g1, m * 0.09, 0.5, '#9FB6F0'],
          [g2, m * 0.15, 0.35, '#C9B6FF'],
        ].forEach(([p, rad, sq, c]) => {
          for (let i = 0; i < galaxy.length; i += 3) {
            const [x, y, b] = galaxy[i];
            ctx.globalAlpha = 0.2 + b * 0.5;
            ctx.fillStyle = Math.hypot(x, y) < 0.25 ? '#F7D98A' : c;
            ctx.fillRect(p[0] + x * rad, p[1] + (y * Math.cos(0.6) + x * 0.2) * rad * sq, R, R);
          }
          ctx.globalAlpha = 1;
          glowDot(ctx, p[0], p[1], rad * 0.4, 'rgba(255,224,160,.5)');
        });
        ctx.strokeStyle = 'rgba(242,193,78,.55)';
        ctx.setLineDash([5 * R, 6 * R]);
        ctx.lineWidth = R;
        ctx.beginPath();
        ctx.moveTo(g1[0] + m * 0.1, g1[1]);
        ctx.lineTo(g2[0] - m * 0.16, g2[1]);
        ctx.stroke();
        ctx.setLineDash([]);
        L('Milky Way', g1[0], g1[1] + m * 0.1, '#F5EEDC');
        L('Andromeda', g2[0], g2[1] + m * 0.12, '#F5EEDC');
        L('2.5 million light-years', cx - m * 0.04, cy - m * 0.1, '#F2C14E');
        break;
      }
      case 7: {
        const rad = m * 0.46;
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, TAU);
        ctx.clip();
        ctx.lineWidth = R;
        for (const [a, b] of web.lines) {
          ctx.strokeStyle = 'rgba(159,182,240,.28)';
          ctx.beginPath();
          ctx.moveTo(cx + a[0] * rad, cy + a[1] * rad);
          ctx.lineTo(cx + b[0] * rad, cy + b[1] * rad);
          ctx.stroke();
        }
        for (const [x, y] of web.pts) {
          ctx.fillStyle = 'rgba(201,182,255,.85)';
          ctx.fillRect(cx + x * rad, cy + y * rad, 1.6 * R, 1.6 * R);
        }
        ctx.restore();
        ctx.strokeStyle = 'rgba(242,193,78,.55)';
        ctx.lineWidth = 1.2 * R;
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, TAU);
        ctx.stroke();
        glowDot(ctx, cx, cy, 5 * R, '#F2C14E');
        L('Us', cx, cy + 18 * R, '#F2C14E');
        break;
      }
      default:
        break;
    }
  }

  class WWCosmicZoom extends LiveCanvas {
    setup() {
      this.canvas = this.querySelector('canvas[data-ww-zoom-canvas]');
      this.range = this.querySelector('input[type="range"][data-ww-zoom-range]');
      if (!this.canvas || !this.range) return;
      this.ctx = this.canvas.getContext('2d');
      this.zi = 0;
      this.zFrom = 0;
      this.zT = 1;
      this.font = getComputedStyle(document.documentElement).getPropertyValue('--font-body-family').trim() || 'sans-serif';
      this.q = (name) => this.querySelector(`[data-ww-zoom="${name}"]`);
      this.onInput = () => this.setStop(Number(this.range.value));
      this.onPrev = () => this.setStop(this.zi - 1);
      this.onNext = () => this.setStop(this.zi + 1);
      this.prev = this.querySelector('[data-ww-zoom-prev]');
      this.next = this.querySelector('[data-ww-zoom-next]');
      this.range.addEventListener('input', this.onInput);
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.range.max = String(STOPS.length - 1);
      this.setStop(Number(this.range.value) || 0, false);
    }

    teardown() {
      if (!this.range) return;
      this.range.removeEventListener('input', this.onInput);
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
    }

    setStop(i, animate = true) {
      i = Math.max(0, Math.min(STOPS.length - 1, i));
      if (i === this.zi && animate) return;
      this.zFrom = this.zi;
      this.zi = i;
      this.zT = animate && !motionOff() ? 0 : 1;
      this.range.value = String(i);
      this.range.setAttribute('aria-valuetext', STOPS[i].name);
      const s = STOPS[i];
      const set = (k, v) => {
        const el = this.q(k);
        if (el) el.textContent = v;
      };
      set('step', `Step ${i + 1} of ${STOPS.length}`);
      set('name', s.name);
      set('size', s.size);
      set('sizeL', s.sizeL);
      set('light', s.light);
      set('say', s.say);
      set('view', s.view);
      if (animate) set('announce', `Step ${i + 1} of ${STOPS.length}: ${s.name}. ${s.size} ${s.sizeL}.`);
      if (this.prev) this.prev.disabled = i === 0;
      if (this.next) this.next.disabled = i === STOPS.length - 1;
      if (!this.raf) this.drawStill();
    }

    resized() {
      if (this.canvas) fit(this.canvas, this.ratio);
    }

    drawStill() {
      this.zT = 1;
      this.frame(performance.now());
    }

    frame(t) {
      if (!this.ctx) return;
      const R = this.ratio;
      const [w, h] = fit(this.canvas, R);
      const ctx = this.ctx;
      ctx.clearRect(0, 0, w, h);
      if (this.zT < 1) this.zT = Math.min(1, this.zT + 0.035);
      const e = 1 - Math.pow(1 - this.zT, 3);
      const out = this.zi > this.zFrom;
      if (this.zT < 1) {
        ctx.save();
        ctx.globalAlpha = 1 - e;
        const s = out ? 1 - 0.7 * e : 1 + 2 * e;
        ctx.translate(w / 2, h / 2);
        ctx.scale(s, s);
        ctx.translate(-w / 2, -h / 2);
        scene(this.zFrom, ctx, w, h, t, R, this.font);
        ctx.restore();
      }
      ctx.save();
      ctx.globalAlpha = e;
      const s2 = out ? 2.4 - 1.4 * e : 0.4 + 0.6 * e;
      ctx.translate(w / 2, h / 2);
      ctx.scale(s2, s2);
      ctx.translate(-w / 2, -h / 2);
      scene(this.zi, ctx, w, h, t, R, this.font);
      ctx.restore();
      ctx.globalAlpha = 1;
    }
  }

  /* ------------------------------------------------------------------
   * Calendar months (painted once, repainted on resize)
   * ------------------------------------------------------------------ */
  function paint(cv, seed, kind, ratio) {
    const [w, h] = fit(cv, ratio);
    const c = cv.getContext('2d');
    const r = rng(seed);
    const pal = [
      ['#1A2150', '#6B4FA0'],
      ['#0B1026', '#3B4F9A'],
      ['#13305A', '#3FB8AF'],
      ['#2A1A4A', '#E8743B'],
      ['#0E1430', '#6B4FA0'],
      ['#1A2150', '#3FB8AF'],
    ][seed % 6];
    const g = c.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, pal[0]);
    g.addColorStop(1, pal[1]);
    c.fillStyle = g;
    c.fillRect(0, 0, w, h);
    for (let i = 0; i < (w * h) / 1800; i++) {
      c.globalAlpha = r() * 0.8;
      c.fillStyle = '#F5EEDC';
      const s = ratio * (r() > 0.95 ? 1.6 : 0.9);
      c.fillRect(r() * w, r() * h * 0.75, s, s);
    }
    c.globalAlpha = 1;
    if (kind === 'moon') {
      glowDot(c, w * 0.66, h * 0.3, w * 0.22, 'rgba(245,238,220,.25)');
      c.fillStyle = '#F2E6C9';
      c.beginPath();
      c.arc(w * 0.66, h * 0.3, w * 0.12, 0, TAU);
      c.fill();
    } else {
      glowDot(c, w * (0.3 + r() * 0.4), h * (0.25 + r() * 0.2), w * 0.25, 'rgba(242,193,78,.35)');
    }
    c.fillStyle = 'rgba(8,10,26,.85)';
    c.beginPath();
    c.moveTo(0, h * 0.82);
    for (let x = 0; x <= w; x += w / 12) c.lineTo(x, h * (0.78 + r() * 0.08));
    c.lineTo(w, h);
    c.lineTo(0, h);
    c.fill();
  }

  class WWCalendarMonths extends HTMLElement {
    connectedCallback() {
      this.draw = this.draw.bind(this);
      requestAnimationFrame(this.draw);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => requestAnimationFrame(this.draw));
        this.ro.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
    }

    draw() {
      const ratio = dpr();
      this.querySelectorAll('canvas').forEach((cv, i) => paint(cv, i + 20, i % 3 === 0 ? 'moon' : '', ratio));
    }
  }

  /* ------------------------------------------------------------------
   * Shop card: option picker updates price, availability and button text
   * ------------------------------------------------------------------ */
  class WWProductCard extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select[name="id"]');
      if (!this.select) return;
      this.onChange = () => this.update();
      this.select.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      if (this.select) this.select.removeEventListener('change', this.onChange);
    }

    update() {
      const opt = this.select.selectedOptions[0];
      if (!opt) return;
      const price = this.querySelector('[data-ww-price]');
      if (price && opt.dataset.price) price.textContent = opt.dataset.price;
      const compare = this.querySelector('[data-ww-compare]');
      if (compare) {
        compare.textContent = opt.dataset.compare || '';
        compare.hidden = !opt.dataset.compare;
      }
      const btn = this.querySelector('button[type="submit"]');
      if (!btn) return;
      const label = btn.querySelector('span');
      const available = opt.dataset.available === 'true';
      btn.disabled = !available;
      if (label && window.variantStrings) {
        label.textContent = available ? btn.dataset.addLabel || window.variantStrings.addToCart : window.variantStrings.soldOut;
      }
    }
  }

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  define('ww-starfield', WWStarfield);
  define('ww-moon-phase', WWMoonPhase);
  define('ww-cosmic-zoom', WWCosmicZoom);
  define('ww-calendar-months', WWCalendarMonths);
  define('ww-product-card', WWProductCard);

  window.WWObservatory = { moonNow, STOPS };
})();
